"use client";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const QuartersSection = ({ data }) => {
  const sectionRef = useRef(null);
  const logoRef = useRef(null);
  const [isClient, setIsClient] = useState(false);
  const [hoveredQuarterPosition, setHoveredQuarterPosition] = useState(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const { scrollYProgress } = useScroll({
    target: isClient ? sectionRef : undefined,
    offset: ["start end", "end start"],
  });

  // Simplified logo rotation
  const logoY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const logoRotate = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const logoScale = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [0.95, 1.1, 0.95]
  );

  // Simplified parallax for all quarters (same as mobile)
  const quartersY = useTransform(scrollYProgress, [0, 1], ["0%", "0%"]);
  const quartersX = useTransform(scrollYProgress, [0, 1], ["0%", "0%"]);

  // Background parallax
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const backgroundScale = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [1, 1.02, 1]
  );
  const backgroundOpacity = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [0.2, 0.25, 0.2]
  );

  // Floating elements
  const floatingElement1Y = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "-20%"]
  );
  const floatingElement2Y = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "20%"]
  );
  const floatingElement3Y = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "-30%"]
  );

  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  // Static fallback data
  const fallbackQuarters = [
    {
      title: "الذكاء الاصطناعي",
      subtitle: "نوظف الذكاء الاصطناعي لتطوير حملاتنا الإعلانية بدقة واحترافية",
      description:
        "نوظف الذكاء الاصطناعي كعنصر أساسي ضمن منهجية العمل في حملاتنا الإعلانية لنعتمد على أنظمة متقدمة تقوم على تحليل البيانات وتطبيق الخوارزميات الذكية وتزويد المحتوى التلقائي، بهدف تحسين دقة الاستهداف ومدى كفاءة التنفيذ وضمان الأثر الإعلاني على مستوى النتائج",
      icon: "🧠",
      image: 'https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/assistant.png',
    },
    {
      title: "تحليل البيانات والإحصائيات",
      subtitle: "نحلل البيانات بدقة لضمان وصول رسالتك للجمهور المناسب",
      description:
        "نعتمد على تحليل البيانات والإحصائيات في تطوير استراتيجياتنا الإعلانية، حيث نقوم بجمع وتحليل المعلومات من مصادر متعددة لفهم سلوك الجمهور المستهدف وتحديد أفضل الأوقات والمنصات للوصول إليهم بفعالية أكبر",
      icon: "📊",
      image: 'https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/analytics.png',
    },
    {
      title: "التصميم الإبداعي والمحتوى المرئي",
      subtitle: "نبدع في التصميم لنجعل علامتك التجارية لا تُنسى",
      description:
        "نقدم خدمات التصميم الإبداعي والمحتوى المرئي المتميز الذي يعكس هوية علامتك التجارية بطريقة جذابة ومؤثرة، مع التركيز على الابتكار والجودة العالية في كل عنصر بصري نقوم بإنتاجه",
      icon: "🎨",
      image: 'https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/design.png',
    },
    {
      title: "إدارة وتحسين الحملات الرقمية",
      subtitle: "نُدير حملاتك الرقمية بخبرة لضمان أفضل النتائج",
      description:
        "نتولى إدارة وتحسين حملاتك الرقمية عبر جميع المنصات الرقمية، مع مراقبة الأداء المستمر وتحليل النتائج لضمان تحقيق أهدافك التسويقية بأعلى مستوى من الكفاءة والفعالية",
      icon: "📱",
      image: 'https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/management.png',
    },
  ];

  // Position mapping based on index
  const positions = ["top-left", "top-right", "bottom-left", "bottom-right"];

  // Calculate arrow rotation angle based on hovered quarter position
  // Arrow starts pointing up (0°), rotates to point at the hovered quarter
  // Fixed mapping: swapped angles based on actual pointing behavior
  const getArrowRotation = (position) => {
    if (!position) return 0;
    switch (position) {
      case "top-left":
        return -45; // Point to top-left (was -135° pointing to bottom-left, now corrected)
      case "top-right":
        return 45; // Point to top-right (was -45° pointing to top-left, now corrected)
      case "bottom-left":
        return -135; // Point to bottom-left (was 135° pointing to bottom-right, now corrected)
      case "bottom-right":
        return 135; // Point to bottom-right (was 45° pointing to top-right, now corrected)
      default:
        return 0; // Point straight up
    }
  };

  // Delay calculation based on index
  const getDelay = (index) => 0.2 + index * 0.2;

  // Process dynamic data or use fallback
  const processQuartersData = () => {
    // Check if data and items exist
    if (
      !data ||
      !data.items ||
      !Array.isArray(data.items) ||
      data.items.length === 0
    ) {
      console.log("Using fallback quarters data");
      return fallbackQuarters.map((item, index) => ({
        ...item,
        position: positions[index % 4],
        delay: getDelay(index),
      }));
    }

    // Process dynamic data
    // Helper to extract string from localized object or return string
    const getLocalizedValue = (value, fallback = "") => {
      if (!value) return fallback;
      if (typeof value === "string") return value;
      if (typeof value === "object" && value !== null) {
        // If it's a localized object {en, ar}, use Arabic (ar) for Arabic page
        return value.ar || value.en || fallback;
      }
      return fallback;
    };

    return data.items.slice(0, 4).map((item, index) => ({
      title: getLocalizedValue(
        item.title,
        fallbackQuarters[index]?.title || "عنوان"
      ),
      subtitle: getLocalizedValue(
        item.subtitle,
        fallbackQuarters[index]?.subtitle || ""
      ),
      description: getLocalizedValue(
        item.description,
        fallbackQuarters[index]?.description || ""
      ),
      icon: item.icon || null,
      image: item.image?.url || item.image || null,
      position: positions[index % 4],
      delay: getDelay(index),
    }));
  };

  const quarters = processQuartersData();

  // Ensure we always have 4 quarters (pad with fallback if needed)
  while (quarters.length < 4) {
    const index = quarters.length;
    quarters.push({
      ...fallbackQuarters[index],
      position: positions[index],
      delay: getDelay(index),
    });
  }

  const getQuarterClasses = (position) => {
    const baseClasses =
      "absolute w-1/2 h-1/2 p-3 sm:p-4 md:p-5 lg:p-6 xl:p-8 flex flex-col justify-center text-center overflow-hidden";
    switch (position) {
      case "top-left":
        return `${baseClasses} top-0 left-0`;
      case "top-right":
        return `${baseClasses} top-0 right-0`;
      case "bottom-left":
        return `${baseClasses} bottom-0 left-0`;
      case "bottom-right":
        return `${baseClasses} bottom-0 right-0`;
      default:
        return baseClasses;
    }
  };

  // Render icon or image
  const renderIconOrImage = (quarter, animated = false) => {
    const content = quarter.image ? (
      <div className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-18 lg:h-18 xl:w-20 xl:h-20 mx-auto mb-2 sm:mb-2.5 md:mb-3">
        <img
          src={quarter.image}
          alt={quarter.title || "Feature"}
          className="object-contain w-full h-full"
          style={{ objectFit: 'contain' }}
          loading="eager"
        />
      </div>
    ) : quarter.icon ? (
      <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl mb-2 sm:mb-2.5 md:mb-3">
        {quarter.icon}
      </div>
    ) : null;

    if (!animated) return content;

    return (
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={isInView ? { scale: 1, opacity: 1 } : {}}
        transition={{
          duration: 0.5,
          delay: quarter.delay + 0.3,
          ease: "easeOut",
        }}
        whileHover={{
          scale: 1.05,
          transition: { duration: 0.2 },
        }}
      >
        {content}
      </motion.div>
    );
  };

  if (!isClient) {
    return (
      <section
        ref={sectionRef}
        className="relative h-screen overflow-hidden"
        style={{
          background: "linear-gradient(0deg, #0C0927 0%, #233C78 100%)",
        }}
      >
        <div className="relative w-full h-full">
          {quarters.map((quarter, index) => (
            <div key={index} className={getQuarterClasses(quarter.position)}>
              <div className="relative z-10 space-y-2 sm:space-y-2.5 md:space-y-3 lg:space-y-4 max-w-xs sm:max-w-sm md:max-w-md mx-auto px-2 sm:px-3 md:px-4">
                {renderIconOrImage(quarter)}
                <h3 className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-white font-neosans mb-1 sm:mb-1.5 md:mb-2">
                  {quarter.title}
                </h3>
                <h4 className="text-xs sm:text-sm md:text-base lg:text-lg text-white/90 font-neosans font-medium mb-2 sm:mb-2.5 md:mb-3 leading-relaxed">
                  {quarter.subtitle}
                </h4>
                <p className="text-xs sm:text-sm md:text-base lg:text-lg text-white/75 font-neosans leading-relaxed">
                  {quarter.description}
                </p>
              </div>
              <div className="absolute inset-0 border border-white/10 rounded-lg" />
            </div>
          ))}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 xl:w-24 xl:h-24 bg-gradient-to-br from-white to-gray-200 rounded-full shadow-2xl flex items-center justify-center border-4 border-white/30 relative overflow-hidden">
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12"
              >
                <path
                  d="M12 2L12 22M12 2L2 12M12 2L22 12"
                  stroke="#1e3a5f"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          <div className="absolute top-0 left-1/2 w-0.5 h-full bg-gradient-to-b from-white/30 via-white/10 to-white/30 transform -translate-x-1/2" />
          <div className="absolute left-0 top-1/2 w-full h-0.5 bg-gradient-to-r from-white/30 via-white/10 to-white/30 transform -translate-y-1/2" />
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="relative h-screen overflow-hidden"
      style={{
        background: "linear-gradient(0deg, #0C0927 0%, #233C78 100%)",
      }}
    >
      {/* Animated Background with Parallax */}
      <motion.div
        className="absolute inset-0"
        style={{
          y: backgroundY,
          scale: backgroundScale,
          opacity: backgroundOpacity,
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(0deg, #0C0927 0%, #233C78 100%)",
          }}
        ></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1)_0%,transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(255,255,255,0.05)_0%,transparent_50%)]"></div>
      </motion.div>

      {/* Floating Geometric Shapes with Simplified Parallax */}
      <motion.div
        className="absolute top-10 left-10 w-16 h-16 bg-white/10 rounded-lg"
        style={{ y: floatingElement1Y }}
        animate={{
          rotate: [0, 360],
          scale: [1, 1.05, 1],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear",
        }}
      />

      <motion.div
        className="absolute top-20 right-20 w-12 h-12 bg-white/15 rounded-full"
        style={{ y: floatingElement2Y }}
        animate={{
          rotate: [0, -360],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "linear",
        }}
      />

      <motion.div
        className="absolute bottom-10 left-20 w-20 h-20 bg-white/5 rounded-xl"
        style={{ y: floatingElement3Y }}
        animate={{
          rotate: [0, 180, 360],
          scale: [0.9, 1, 0.9],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="absolute bottom-20 right-10 w-8 h-8 bg-white/20 rounded-full"
        style={{ y: floatingElement1Y }}
        animate={{
          x: [0, 15, 0],
          y: [0, -8, 0],
          opacity: [0.2, 0.6, 0.2],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Quarters with Simplified Animation */}
      <div className="relative w-full h-full">
        {quarters.map((quarter, index) => (
          <motion.div
            key={index}
            className={getQuarterClasses(quarter.position)}
            style={{
              y: quartersY,
              x: quartersX,
            }}
            initial={{
              opacity: 0,
              scale: 0.9,
              y: 30,
            }}
            animate={
              isInView
                ? {
                    opacity: 1,
                    scale: 1,
                    y: 0,
                  }
                : {}
            }
            transition={{
              duration: 0.8,
              delay: quarter.delay,
              ease: "easeOut",
            }}
            whileHover={{
              scale: 1.01,
              transition: { duration: 0.2, ease: "easeOut" },
            }}
            onMouseEnter={() => setHoveredQuarterPosition(quarter.position)}
            onMouseLeave={() => setHoveredQuarterPosition(null)}
          >
            {/* Inner Content Container */}
            <motion.div
              className="relative z-10 space-y-2 sm:space-y-2.5 md:space-y-3 lg:space-y-4 max-w-xs sm:max-w-sm md:max-w-md mx-auto px-2 sm:px-3 md:px-4"
              initial={{ y: 20, opacity: 0 }}
              animate={isInView ? { y: 0, opacity: 1 } : {}}
              transition={{
                duration: 0.6,
                delay: quarter.delay + 0.2,
                ease: "easeOut",
              }}
            >
              {/* Animated Icon or Image */}
              {renderIconOrImage(quarter, true)}

              {/* Title */}
              <motion.h3
                className="text-sm sm:text-base md:text-lg lg:text-2xl  font-bold text-white font-neosans mb-1 sm:mb-1.5 md:mb-2"
                initial={{ opacity: 0, y: 15 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 0.5,
                  delay: quarter.delay + 0.4,
                  ease: "easeOut",
                }}
                style={{
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  direction: "rtl",
                  textAlign: "center",
                }}
              >
                {quarter.title}
              </motion.h3>

              {/* Subtitle */}
              {quarter.subtitle && (
                <motion.h4
                  className="text-xs sm:text-sm md:text-base lg:text-md text-white/80 font-neosans font-medium mb-2 sm:mb-2.5 md:mb-3 leading-relaxed"
                  initial={{ opacity: 0, y: 15 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{
                    duration: 0.5,
                    delay: quarter.delay + 0.5,
                    ease: "easeOut",
                  }}
                  style={{
                    direction: "rtl",
                    textAlign: "center",
                  }}
                >
                  {quarter.subtitle}
                </motion.h4>
              )}

              {/* Description */}
              {quarter.description && (
                <motion.p
                  className="text-xs sm:text-sm md:text-base lg:text-sm text-white/75 font-neosans leading-relaxed"
                  initial={{ opacity: 0, y: 15 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{
                    duration: 0.5,
                    delay: quarter.delay + 0.6,
                    ease: "easeOut",
                  }}
                  style={{
                    direction: "rtl",
                    textAlign: "center",
                  }}
                >
                  {quarter.description}
                </motion.p>
              )}
            </motion.div>

            {/* Border */}
            <motion.div
              className="absolute inset-0 border border-white/10 rounded-lg"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{
                duration: 0.6,
                delay: quarter.delay + 0.3,
                ease: "easeOut",
              }}
            />

            {/* Simplified corner accent */}
            <motion.div
              className="absolute top-2 right-2 w-8 h-8 bg-gradient-to-bl from-white/10 to-transparent rounded-bl-full"
              initial={{ opacity: 0, scale: 0 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{
                duration: 0.5,
                delay: quarter.delay + 0.7,
                ease: "easeOut",
              }}
            />
          </motion.div>
        ))}

        {/* Central Arrow with Smooth Rotation */}
        <motion.div
          ref={logoRef}
          className=" absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20"
          style={{
            y: logoY,
            scale: logoScale,
          }}
        >
          <motion.div
            className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-white to-gray-200 rounded-full shadow-2xl flex items-center justify-center border-4 border-white/30 relative overflow-hidden"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{
              duration: 0.8,
              delay: 1,
              ease: "easeOut",
            }}
          >
            {/* Simplified background pattern */}
            <motion.div
              className=" absolute inset-0 bg-gradient-to-br from-[#1e3a5f]/20 to-transparent"
              animate={{ rotate: [0, 360] }}
              transition={{
                duration: 30,
                repeat: Infinity,
                ease: "linear",
              }}
            />

            {/* Arrow Icon - Rotates to point at hovered quarter */}
            <motion.div
              className="w-full h-full flex items-center justify-center z-10 relative"
              animate={{
                rotate: getArrowRotation(hoveredQuarterPosition),
              }}
              transition={{
                duration: 0.5,
                ease: "easeInOut",
              }}
            >
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10"
              >
                <path
                  d="M12 2L12 22M12 2L2 12M12 2L22 12"
                  stroke="#1e3a5f"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Divider Lines with Simplified Animation */}
        <motion.div
          className="absolute top-0 left-1/2 w-0.5 h-full bg-gradient-to-b from-white/30 via-white/10 to-white/30 transform -translate-x-1/2"
          initial={{ scaleY: 0, opacity: 0 }}
          animate={isInView ? { scaleY: 1, opacity: 1 } : {}}
          transition={{
            duration: 1,
            delay: 0.3,
            ease: "easeOut",
          }}
        />
        <motion.div
          className="absolute left-0 top-1/2 w-full h-0.5 bg-gradient-to-r from-white/30 via-white/10 to-white/30 transform -translate-y-1/2"
          initial={{ scaleX: 0, opacity: 0 }}
          animate={isInView ? { scaleX: 1, opacity: 1 } : {}}
          transition={{
            duration: 1,
            delay: 0.5,
            ease: "easeOut",
          }}
        />

        {/* Corner Accents */}
        {[
          { position: "top-0 left-0", border: "border-t-2 border-l-2" },
          { position: "top-0 right-0", border: "border-t-2 border-r-2" },
          { position: "bottom-0 left-0", border: "border-b-2 border-l-2" },
          { position: "bottom-0 right-0", border: "border-b-2 border-r-2" },
        ].map((corner, i) => (
          <motion.div
            key={i}
            className={`absolute ${corner.position} w-8 h-8 ${corner.border} border-white/20`}
            initial={{ opacity: 0, scale: 0 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{
              duration: 0.5,
              delay: 1 + i * 0.1,
              ease: "easeOut",
            }}
          />
        ))}
      </div>

      {/* Overlay Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-black/10 pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(30,58,95,0.1)_100%)] pointer-events-none"></div>
    </section>
  );
};

export default QuartersSection;
